<template>
  <div class="bb-result">
    <BbScoreBar />

    <div class="content">
      <header class="round-head">
        <h1 class="title">
          Ronde {{ activeRoundNumber }} <span class="of">van {{ totalRounds }}</span>
        </h1>
        <p class="subtitle">
          {{ activeCards }} {{ activeCards === 1 ? 'slag' : 'slagen' }} te verdelen
          <span v-if="trump" class="trump-chip" :class="{ red: trump.red }">
            {{ trump.symbol }} {{ trump.label }}
          </span>
        </p>
      </header>

      <BbPlayerGrid class="results" :count="biddingOrder.length">
        <section
          v-for="player in biddingOrder"
          :key="player.id"
          class="card"
          :class="{ pending: tricks[player.id] === null || tricks[player.id] === undefined }"
        >
          <div class="result-head">
            <span class="result-name">{{ player.name }}</span>
            <span v-if="player.id === activeDealerId" class="dealer-tag">Deler</span>
            <span class="bid-chip">bod {{ bids[player.id] }}</span>
            <span
              v-if="tricks[player.id] !== null && tricks[player.id] !== undefined"
              class="points-badge"
              :class="{ positive: preview(player.id) > 0, negative: preview(player.id) < 0 }"
            >{{ preview(player.id) > 0 ? '+' : '' }}{{ preview(player.id) }}</span>
          </div>
          <BbNumberPicker
            :model-value="tricks[player.id] ?? null"
            :max="activeCards"
            :aria-label="'Gehaalde slagen van ' + player.name"
            @update:model-value="setTricks(player.id, $event)"
          />
        </section>
      </BbPlayerGrid>

      <p class="total-line" :class="{ warn: allFilled && totalTricks !== activeCards }">
        Totaal gehaald: <strong>{{ totalTricks }}</strong> van {{ activeCards }}
        <span v-if="allFilled && totalTricks !== activeCards" class="total-hint">
          — er moeten precies {{ activeCards }} slagen verdeeld zijn
        </span>
      </p>
    </div>

    <AppFooter
      :can-continue="canContinue"
      :show-back="true"
      @continue="submit"
      @back="backToOverview"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import { roundPoints, trumpById } from '../../models/boerenbridge'
import BbScoreBar from './BbScoreBar.vue'
import BbNumberPicker from './BbNumberPicker.vue'
import BbPlayerGrid from './BbPlayerGrid.vue'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'BbResult',
  components: { BbScoreBar, BbNumberPicker, BbPlayerGrid, AppFooter },
  created() {
    this.ensureRoundDraft()
  },
  computed: {
    ...mapState(useBoerenbridgeStore, [
      'players',
      'biddingOrder',
      'activeRoundNumber',
      'activeCards',
      'activeDealerId',
      'totalRounds',
      'currentBid',
      'currentTricks',
      'editingRoundNumber'
    ]),
    // Ingevoerde slagen leven in de store, zodat ze bewaard blijven wanneer je
    // terug naar het bod gaat of de pagina ververst
    tricks() {
      return this.currentTricks ?? {}
    },
    bids() {
      return this.currentBid?.bids ?? {}
    },
    trump() {
      return trumpById(this.currentBid?.trump)
    },
    allFilled() {
      return this.players.every(p => this.tricks[p.id] !== null && this.tricks[p.id] !== undefined)
    },
    totalTricks() {
      return this.players.reduce((sum, p) => sum + (this.tricks[p.id] ?? 0), 0)
    },
    canContinue() {
      return this.allFilled && this.totalTricks === this.activeCards
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, [
      'ensureRoundDraft',
      'setTricks',
      'recordRoundResult',
      'updateLastRoundResult',
      'backToOverview'
    ]),
    preview(playerId) {
      return roundPoints(this.bids[playerId] ?? 0, this.tricks[playerId] ?? 0)
    },
    submit() {
      if (!this.canContinue) return
      this.editingRoundNumber === null
        ? this.recordRoundResult()
        : this.updateLastRoundResult()
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.canContinue) this.submit()
      if (e.key === 'Escape') this.backToOverview()
    }
  }
}
</script>

<style scoped>
.bb-result {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.round-head { text-align: center; }

.title {
  font-size: 1.5rem;
  color: #2c3e50;
}

.of {
  font-size: 1rem;
  font-weight: 400;
  color: #5a6d5a;
}

.subtitle {
  font-size: 0.9rem;
  color: #5a6d5a;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.trump-chip {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2c3e50;
  background: #eef5ee;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}

.trump-chip.red { color: #c0392b; }

.card {
  width: 100%;
  max-width: min(560px, 100%);
  background: #fff;
  border: 2px solid #aac8aa;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.card.pending { border-style: dashed; }

.result-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dealer-tag {
  flex: 0 0 auto;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: #2c3e50;
  border-radius: 999px;
  padding: 0.15rem 0.45rem;
}

.bid-chip {
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 700;
  color: #5a6d5a;
  background: #eef5ee;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
}

.points-badge {
  margin-left: auto;
  display: inline-block;
  min-width: 2.75rem;
  text-align: center;
  padding: 0.25em 0.5em;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 700;
  background: #f0f0f0;
  color: #999;
}

.points-badge.positive { background: #e6f6ed; color: #27ae60; }
.points-badge.negative { background: #fdecea; color: #c0392b; }

.total-line {
  font-size: 0.9rem;
  color: #5a6d5a;
  text-align: center;
}

.total-line.warn {
  color: #c0392b;
  font-weight: 600;
}

.total-hint { font-size: 0.8rem; }

/* Op tablet staan de spelers in een raster dat bij hun aantal past (2×2 bij
   vier, 3×3 bij negen). */
@media (min-width: 600px) {
  .content {
    padding: 1.5rem;
    gap: 1rem;
    justify-content: safe center;
  }

  .results { max-width: 64rem; }

  .title { font-size: 1.85rem; }

  .subtitle { font-size: 1.05rem; }

  .trump-chip {
    font-size: 0.95rem;
    padding: 0.2rem 0.65rem;
  }

  .card {
    max-width: none;
    padding: 1rem 1.25rem;
    gap: 0.75rem;
    border-radius: 14px;
  }

  /* Bij smalle vakken zakken bod en punten onder de naam, die blijft leesbaar */
  .result-head {
    flex-wrap: wrap;
    gap: 0.4rem 0.65rem;
  }

  .result-name { font-size: 1.3rem; }

  .dealer-tag {
    font-size: 0.7rem;
    padding: 0.2rem 0.55rem;
  }

  .bid-chip {
    font-size: 0.9rem;
    padding: 0.25rem 0.65rem;
  }

  .points-badge {
    min-width: 3.25rem;
    font-size: 1.1rem;
    border-radius: 8px;
  }

  .total-line { font-size: 1.05rem; }

  .total-hint { font-size: 0.95rem; }
}
</style>
